// Single source of truth for resolving API URLs from VITE_API_URL.
//
// The whole app reads ONE base URL (the th_api_gateway) and discriminates the
// destination by path prefix, not by env var:
//   - Core (non-reconciliation) screens → `${VITE_API_URL}/api/core/...`
//   - Reconciliation screens            → `${VITE_API_URL}/api/reconciliations/...`
//   - Ingestion (parse-templates)       → `${VITE_API_URL}/api/ingestion/...`
//   - Status board (tenant_auth_module) → `${VITE_API_URL}/api/tenant-auth/...`
//   - AI intents (ai_orchestrator)      → `${VITE_API_URL}/api/ai/...`
//   - Reports (th_core_reporting)       → `${VITE_API_URL}/api/reporting/...`
//   - Datalake dashboards (UC-100)      → `${VITE_API_URL}/api/datalake/...`
//   - A confirmed AI proposal            → `${VITE_API_URL}` + the route the
//                                          proposal carries, unchanged (gatewayUrl)
const CORE_PREFIX = '/api/core'
const RECONCILIATION_PREFIX = '/api/reconciliations'
const INGESTION_PREFIX = '/api/ingestion'
const SERVICES_PREFIX = '/api/services/services'
const CONNECTIONS_PREFIX = '/api/services/connections'
const INTEGRATIONS_PREFIX = '/api/integrations'
const REPORTING_PREFIX = '/api/reporting'
const DATALAKE_PREFIX = '/api/datalake'

// The board module exposes `/api/board` on its own host. The PUBLIC path
// through th_api_gateway is `/api/tenant-auth/board` (UC-048 BR12), confirmed
// against the gateway's own `frontend_delivery_board` route: it matches
// `/api/tenant-auth/board/{**catch-all}` for GET/OPTIONS only and applies
// `PathRemovePrefix: /api/tenant-auth`, so with the `tenant_auth_api` cluster
// pointing at `…:5238/api/` the module receives `/api/board`. Kept in one
// constant, and deliberately not an env var, because Vite inlines VITE_* at
// build time and a rebuild would be needed either way.
const BOARD_PREFIX = '/api/tenant-auth'

// The AI console's own prefix (UC-052). There used to be a second one for the
// module's AI surface; the module's manifest now declares PUBLIC routes and the
// proposal carries them whole, so nothing needs to build that path any more —
// see `gatewayUrl` below (UC-054).
const AI_PREFIX = '/api/ai'

function resolveBaseUrl(): string {
  const baseUrl = import.meta.env.VITE_API_URL
  if (!baseUrl) {
    // Fail loud if the env var is missing in this build. Without this guard,
    // fetch resolves the path against the SPA's own origin, which would
    // forward the bearer token to the wrong host and surface as confusing
    // 404/401 errors (and silent logout on 401).
    throw new Error(
      'VITE_API_URL is not configured. Set the gateway URL in your .env file ' +
        '(e.g., https://sandbox.api.gateway.treasuryhub.ai).',
    )
  }
  return baseUrl
}

// Core call sites pass paths beginning with `/api/` (e.g. '/api/Users/tenants').
// We rewrite that leading `/api/` to `/api/core/` so existing call sites need no
// changes. The regex is anchored (^) and only replaces the first `/api/`, so
// nested segments survive: '/api/apiAccess/clientId' → '/api/core/apiAccess/clientId'.
export function coreUrl(path: string): string {
  return `${resolveBaseUrl()}${CORE_PREFIX}${path.replace(/^\/api\//, '/')}`
}

// Reconciliation call sites pass paths beginning with `/v1/`, so we only prepend
// the prefix: '/v1/Movements' → '<base>/api/reconciliations/v1/Movements'.
export function reconciliationUrl(path: string): string {
  return `${resolveBaseUrl()}${RECONCILIATION_PREFIX}${path}`
}

// Ingestion call sites pass paths beginning with `/parse-templates/`, so we only
// prepend the prefix: '/parse-templates/123' → '<base>/api/ingestion/parse-templates/123'.
// The gateway routes `/api/ingestion/parse-templates/{**catch-all}` to th_ingestion.
export function ingestionUrl(path: string): string {
  return `${resolveBaseUrl()}${INGESTION_PREFIX}${path}`
}

// Control plane of th_services. The gateway's `/api/services/{**catch-all}` strips the WHOLE
// prefix, so the next segment names the downstream controller — hence the double segment:
// '/tenant' → '<base>/api/services/services/tenant' → th_services' api/services/tenant. One
// gateway route reaches every controller of the microservice; a new one needs no gateway change.
export function servicesUrl(path: string): string {
  return `${resolveBaseUrl()}${SERVICES_PREFIX}${path}`
}

// The tenant's connections in th_services — the source of truth for whether a service is connected
// (the #12 Connections model). Same namespaced route as servicesUrl:
// '?serviceCode=kuspit' → '<base>/api/services/connections?serviceCode=kuspit' →
// th_services' api/connections.
export function connectionsUrl(path: string): string {
  return `${resolveBaseUrl()}${CONNECTIONS_PREFIX}${path}`
}

// One integration's own HTTP surface. The gateway routes
// `/api/integrations/<code>/{**catch-all}` to that integration's host and strips the whole
// prefix, so '/auth/login' lands on the integration's own `api/auth/login`. The code is a
// parameter on purpose: adding a provider must not require touching this file.
export function integrationUrl(integrationCode: string, path: string): string {
  return `${resolveBaseUrl()}${INTEGRATIONS_PREFIX}/${integrationCode}${path}`
}

// th_core_reporting (read-only reports). Call sites pass the service's own path without its
// `/api`, so '/balances/difference' → '<base>/api/reporting/balances/difference'. The gateway's
// `frontend_reporting` route strips `/api/reporting` and the `reporting_api` cluster points at
// `…/api/`, so the service receives `/api/balances/difference`.
export function reportingUrl(path: string): string {
  return `${resolveBaseUrl()}${REPORTING_PREFIX}${path}`
}

// Datalake call sites pass paths beginning with `/v1/`, so we only prepend the
// prefix: '/v1/accounts' → '<base>/api/datalake/v1/accounts'. The gateway strips
// `/api/datalake` and forwards to the datalake reporting API (UC-100).
export function datalakeUrl(path: string): string {
  return `${resolveBaseUrl()}${DATALAKE_PREFIX}${path}`
}

// Board call sites pass paths beginning with `/board`, so we only prepend the
// prefix: '/board' → '<base>/api/tenant-auth/board'.
export function boardUrl(path: string): string {
  return `${resolveBaseUrl()}${BOARD_PREFIX}${path}`
}

// AI intents (th_ai_orchestrator) → `${VITE_API_URL}/api/ai/...`.
// The orchestrator has NO ingress of its own, on purpose: if a request does not
// go through th_api_gateway, it does not reach the service. The gateway stamps
// X-Tenant-Id / X-User-Id from the identity it resolved and — unlike the board
// route — deliberately does NOT strip Authorization, because the orchestrator
// resolves identifiers on-behalf-of the user with the user's own token. That is
// what makes "the agent cannot reach anything the person could not reach alone"
// true rather than aspirational (UC-052).
export function aiUrl(path: string): string {
  return `${resolveBaseUrl()}${AI_PREFIX}${path}`
}

// A path that is already the public path, sent as it stands. Prepends NOTHING.
//
// This exists for ONE caller: `publishProposal`, publishing a route that came
// from the orchestrator's manifest with `/api/tenant-auth/admin/ai` already in
// it (UC-054 BR4). It replaces the old `adminAiUrl`, which prepended the gateway
// prefix while `publishProposal` stripped the module's — two strings coupled by
// hand, each with a comment warning that changing one obliged the other.
//
// Do NOT reach for this as a general-purpose helper. "Call any path on the
// gateway with the user's bearer" is exactly the primitive that a route chosen
// by a language model must not have; what makes it safe here is that the route
// has already passed the allowlist in `ai-intent-api.ts`. A new call site needs
// its own reason, and probably its own prefixed function above.
export function gatewayUrl(path: string): string {
  return `${resolveBaseUrl()}${path}`
}
